import type { AnswerEntry, AnswerValue, QuestionSchema } from '@/api/miniapp/types';

/**
 * Правила ответов анкеты.
 *
 * Отдельно от отрисовки: обязательность, границы множественного выбора и
 * заполненность «своего варианта» нужны и полю, и экрану анкеты. Пока правила
 * жили рядом с разметкой, экран легко мог решить иначе, чем поле.
 */

export interface QuestionDraft {
  value: AnswerValue;
  otherValue: string;
  comment: string;
}

export function draftOf(entry: AnswerEntry | undefined): QuestionDraft {
  return {
    value: entry?.value ?? null,
    otherValue: entry?.otherValue ?? '',
    comment: entry?.comment ?? '',
  };
}

/** Выбран ли вариант «свой вариант», открывающий дополнительное поле. */
export function isOtherSelected(question: QuestionSchema, value: AnswerValue): boolean {
  const trigger = question.other?.triggerValue;
  if (!trigger) return false;
  if (Array.isArray(value)) return value.includes(trigger);
  return value === trigger;
}

/**
 * Выбран ли ответ «нечего указать» («Профессиональных страниц пока нет»).
 *
 * Он хранится пустым списком, а ещё не отвеченный вопрос — отсутствием
 * значения: только так явный выбор отличается от пропуска.
 */
export function isEmptyAnswerChosen(question: QuestionSchema, value: AnswerValue): boolean {
  return question.allowEmptyAnswer === true && Array.isArray(value) && value.length === 0;
}

/**
 * Есть ли у вопроса сохранённый ответ — для счётчика заполненности.
 *
 * Считается сам факт ответа, а не его соответствие правилам поля: иначе
 * анкета, пройденная до конца, показывала «15 из 24», хотя ответы на месте.
 */
export function hasSavedAnswer(question: QuestionSchema, entry: AnswerEntry | undefined): boolean {
  const value = entry?.value;
  if (entry?.otherValue?.trim()) return true;
  if (value == null) return false;
  if (Array.isArray(value)) {
    return isEmptyAnswerChosen(question, value) || value.some((item) => typeof item === 'string' && item.trim());
  }
  if (typeof value === 'object') {
    return Object.values(value).some((item) => typeof item === 'string' && item.trim());
  }
  return String(value).trim().length > 0;
}

/**
 * Готов ли ответ к отправке. Обязательность, границы множественного выбора и
 * заполненность поля «свой вариант» проверяются здесь, чтобы экран не решал
 * это сам и не расходился с правилами схемы.
 */
/**
 * Вопросы, которые можно оставить пустыми.
 *
 * Схема помечает обязательными лишь несколько вопросов, хотя анкета целиком
 * нужна ассистенту для работы. Правило держится здесь, чтобы не расходиться
 * между экранами; у бэкенда запрошены корректные флаги `required`.
 */
const OPTIONAL_KEYS = new Set(['user_gender', 'additional_specialization']);

export function isRequired(question: QuestionSchema): boolean {
  return !OPTIONAL_KEYS.has(question.key);
}

/**
 * Пустой ответ на вопрос, который разрешено пропустить.
 *
 * Такой ответ не отправляется: бэкенд пустое значение не принимает и
 * возвращает ошибку, а человек видел «Ответ не сохранён» на вопросе, который
 * помечен необязательным.
 */
export function isSkippedAnswer(question: QuestionSchema, draft: QuestionDraft): boolean {
  if (isRequired(question)) return false;
  return !hasSavedAnswer(question, {
    value: draft.value,
    otherValue: draft.otherValue,
    comment: draft.comment,
  });
}

export function isAnswered(question: QuestionSchema, draft: QuestionDraft): boolean {
  const { value } = draft;
  const required = isRequired(question);

  if (question.other?.required && isOtherSelected(question, value) && !draft.otherValue.trim()) {
    return false;
  }

  if (question.type === 'multi_select' || question.type === 'list') {
    // В сохранённом ответе могут оказаться null: сервер хранит список как есть
    const list = Array.isArray(value)
      ? value.filter((item) => typeof item === 'string' && item.trim())
      : [];
    // Пустой список допустим, только если врач явно выбрал ответ «нечего
    // указать»: без выбора «Далее» пропускала вопрос незаполненным
    if (list.length === 0) return isEmptyAnswerChosen(question, value) || !required;
    // Во множественном выборе хватает одного варианта: схема присылает `min`
    // больше единицы (вопрос о возражениях — три), и врач застревал на вопросе,
    // где подходит один ответ. Для списка минимум схемы по-прежнему действует
    if (question.type === 'multi_select') return true;
    if (question.min && list.length < question.min) return false;
    return true;
  }

  if (question.type === 'group') {
    const record = (value ?? {}) as Record<string, string | null>;
    return (question.fields ?? [])
      .filter((field) => field.required)
      .every((field) => Boolean(record[field.key]?.trim()));
  }

  if (!required) return true;
  return typeof value === 'string' && value.trim().length > 0;
}
